import React, { useMemo } from 'react';
import './CoachComposition.css';
interface CoachCompositionProps {
  trainNumber: string;
  classes: string[]; // e.g. ['SL', '3A', '2A', '1A']
}

interface Coach {
  id: string;
  type: string; // 'ENG', 'SLR', 'GS', 'SL', '3A', '2A', '1A', 'PC', 'CC'
  label: string;
}

const generateRealisticRake = (classes: string[]): Coach[] => {
  const coaches: Coach[] = [];
  
  // Engine
  coaches.push({ id: 'ENG', type: 'ENG', label: 'LOCO' });
  coaches.push({ id: 'SLR1', type: 'SLR', label: 'SLR' });
  coaches.push({ id: 'GS1', type: 'GS', label: 'GEN' });
  coaches.push({ id: 'GS2', type: 'GS', label: 'GEN' });

  // Sleeper
  if (classes.includes('SL')) {
    for (let i = 1; i <= 7; i++) {
      coaches.push({ id: `S${i}`, type: 'SL', label: `S${i}` });
    }
  }

  // Chair Car
  if (classes.includes('CC')) {
    for (let i = 1; i <= 4; i++) {
      coaches.push({ id: `C${i}`, type: 'CC', label: `C${i}` });
    }
  }

  // Pantry Car - usually placed between Non-AC and AC
  coaches.push({ id: 'PC', type: 'PC', label: 'PC' });

  // 3 Tier AC
  if (classes.includes('3A')) {
    for (let i = 1; i <= 5; i++) {
      coaches.push({ id: `B${i}`, type: '3A', label: `B${i}` });
    }
  }

  // 2 Tier AC
  if (classes.includes('2A')) {
    for (let i = 1; i <= 2; i++) {
      coaches.push({ id: `A${i}`, type: '2A', label: `A${i}` });
    }
  }

  // 1st Class AC
  if (classes.includes('1A')) {
    coaches.push({ id: 'H1', type: '1A', label: 'H1' });
  }

  coaches.push({ id: 'GS3', type: 'GS', label: 'GEN' });
  coaches.push({ id: 'SLR2', type: 'SLR', label: 'SLR' });

  return coaches;
};

const getCoachColor = (type: string) => {
  switch (type) {
    case 'ENG': return 'var(--color-primary)';
    case 'SLR': return '#64748b'; // slate
    case 'GS':  return '#94a3b8'; // light slate
    case 'SL':  return '#3b82f6'; // blue
    case '3A':  return '#8b5cf6'; // purple
    case '2A':  return '#d946ef'; // fuchsia
    case '1A':  return '#f43f5e'; // rose
    case 'CC':  return '#06b6d4'; // cyan
    case 'PC':  return '#f59e0b'; // amber
    default:    return '#cbd5e1';
  }
};

const getCoachFullName = (type: string) => {
  switch (type) {
    case 'ENG': return 'Engine';
    case 'SLR': return 'Seating cum Luggage Rake';
    case 'GS':  return 'General Unreserved';
    case 'SL':  return 'Sleeper Class';
    case '3A':  return 'Third AC';
    case '2A':  return 'Second AC';
    case '1A':  return 'First AC';
    case 'CC':  return 'AC Chair Car';
    case 'PC':  return 'Pantry Car';
    default:    return 'Coach';
  }
};

const CoachComposition: React.FC<CoachCompositionProps> = ({ classes }) => {
  const rake = useMemo(() => generateRealisticRake(classes), [classes]);

  return (
    <div className="coach-composition-wrapper">
      <div className="coach-legend">
        <span className="legend-title">Train Composition (Indicator)</span>
        <div className="legend-items">
          {Array.from(new Set(rake.map(c => c.type))).map(type => (
            <div key={type} className="legend-item">
              <span className="legend-dot" style={{ background: getCoachColor(type) }}></span>
              <span>{getCoachFullName(type)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="coach-track-container">
        <div className="coach-rail-track" aria-hidden="true" />
        <div className="coach-scroll-area">
          {rake.map((coach, index) => (
            <div key={index} className="coach-car-wrapper">
              <div 
                className={`coach-car ${coach.type === 'ENG' ? 'coach-engine' : ''}`}
                style={{ '--coach-color': getCoachColor(coach.type) } as React.CSSProperties}
                title={getCoachFullName(coach.type)}
              >
                <div className="coach-roof" />
                <div className="coach-windows">
                  {coach.type !== 'ENG' && (
                    <>
                      <div className="window" />
                      <div className="window" />
                      <div className="window" />
                    </>
                  )}
                  {coach.type === 'ENG' && <div className="window engine-window" />}
                </div>
                <div className="coach-label">{coach.label}</div>
                <div className="coach-wheels">
                  <div className="wheel" />
                  <div className="wheel" />
                </div>
              </div>
              {/* Connector between coaches */}
              {index < rake.length - 1 && <div className="coach-connector" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoachComposition;
